import { z } from 'zod';

/**
 * The client environment contract.
 *
 * Deliberately kept free of `import.meta` so it can be imported from two very
 * different places:
 *
 * - `vite.config.ts`, at build time, where it turns a missing variable into a
 *   failed build rather than a blank screen three clicks into the app;
 * - `src/lib/env.ts`, at runtime, where it hands the application a typed,
 *   already-validated object.
 *
 * Only `VITE_`-prefixed variables exist here: Vite inlines nothing else into the
 * bundle, and anything that is not inlined cannot be read by a browser.
 */

/** An absolute `http(s)` URL — a relative or protocol-less value is rejected. */
const absoluteHttpUrl = (label: string) =>
  z
    .string()
    .min(1, `${label} must not be empty`)
    .refine(
      (value) => {
        try {
          const { protocol } = new URL(value);
          return protocol === 'http:' || protocol === 'https:';
        } catch {
          return false;
        }
      },
      `${label} must be an absolute http(s) URL, e.g. http://localhost:3000`,
    );

export const clientEnvSchema = z.object({
  /**
   * Base URL of the Atlas REST API, including any path prefix but *not* the
   * `/v1` segment — the axios client appends that.
   */
  VITE_API_URL: absoluteHttpUrl('VITE_API_URL'),

  /**
   * Origin serving uploaded prototype files. This is a security boundary, not a
   * convenience: prototype JavaScript runs here so the same-origin policy keeps
   * it away from the application's cookies, storage and DOM. It is validated as
   * a bare origin because every `postMessage` check compares `event.origin`
   * against it, and `event.origin` never carries a path.
   */
  VITE_SANDBOX_ORIGIN: absoluteHttpUrl('VITE_SANDBOX_ORIGIN').refine(
    (value) => {
      try {
        return new URL(value).origin === value.replace(/\/+$/, '');
      } catch {
        return false;
      }
    },
    'VITE_SANDBOX_ORIGIN must be a bare origin with no path, e.g. http://localhost:3001',
  ),
});

/** The validated client environment. */
export type ClientEnv = z.infer<typeof clientEnvSchema>;

/** Every variable the schema requires, for error messages and `.env` templates. */
export const CLIENT_ENV_KEYS = Object.keys(
  clientEnvSchema.shape,
) as ReadonlyArray<keyof ClientEnv>;

/**
 * Render a failed parse as a single readable block.
 *
 * Used verbatim by both callers so a broken `.env` reads the same whether it is
 * caught by the build or by the bundle.
 */
export const formatEnvError = (error: z.ZodError): string => {
  const lines = error.issues.map((issue) => {
    const key = issue.path.join('.') || '(root)';
    return `  - ${key}: ${issue.message}`;
  });

  return [
    'Invalid client environment.',
    ...lines,
    '',
    'Copy `apps/web/.env.example` to `apps/web/.env` and fill in every value.',
  ].join('\n');
};

/**
 * Parse an arbitrary record against {@link clientEnvSchema}, throwing a
 * readable `Error` on failure.
 */
export const parseClientEnv = (source: Record<string, unknown>): ClientEnv => {
  const result = clientEnvSchema.safeParse(source);

  if (!result.success) {
    throw new Error(formatEnvError(result.error));
  }

  return result.data;
};
